import React, { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { heroContent, fadeInUp, staggerContainer, floatingAnimation, swayAnimation, pulseAnimation } from '../data/constants';
import AnimatedCounter from './AnimatedCounter';
import ParallaxElement from './ParallaxElement';

interface HeroProps {
    onTrialClick: () => void;
    onViewPlansClick: () => void;
}

const Hero: React.FC<HeroProps> = ({ onTrialClick, onViewPlansClick }) => {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end start"]
    });

    // Mouse tracking for magnetic effects
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const springX = useSpring(mouseX, { stiffness: 300, damping: 30 });
    const springY = useSpring(mouseY, { stiffness: 300, damping: 30 });

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            const { clientX, clientY } = e;
            const { innerWidth, innerHeight } = window;
            mouseX.set((clientX - innerWidth / 2) * 0.02);
            mouseY.set((clientY - innerHeight / 2) * 0.02);
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [mouseX, mouseY]);

    // Parallax transforms
    const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);

    return (
        <section
            ref={containerRef}
            id="home"
            className="relative min-h-screen bg-white overflow-hidden"
        >
            {/* Enhanced Background decorative elements */}
            <div className="absolute inset-0 z-0">
                {/* Floating Musical Notes with Parallax */}
                <ParallaxElement speed={0.3} direction="up">
                    <motion.div
                        animate={floatingAnimation}
                        style={{ x: springX, y: springY }}
                        className="absolute top-20 left-10 text-brand-gold text-4xl opacity-20"
                    >
                        ♪
                    </motion.div>
                </ParallaxElement>

                <ParallaxElement speed={0.5} direction="down">
                    <motion.div
                        animate={{
                            y: [0, -15, 0],
                            rotate: [0, -5, 0],
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 1,
                        }}
                        style={{
                            x: useTransform(springX, [-10, 10], [-5, 5]),
                            y: useTransform(springY, [-10, 10], [-5, 5])
                        }}
                        className="absolute top-32 left-1/4 text-brand-dark-red text-3xl opacity-15"
                    >
                        ♫
                    </motion.div>
                </ParallaxElement>

                <ParallaxElement speed={0.7} direction="up">
                    <motion.div
                        animate={swayAnimation}
                        className="absolute bottom-40 left-20 text-brand-gold text-5xl opacity-10"
                    >
                        ♬
                    </motion.div>
                </ParallaxElement>

                {/* Enhanced Om Symbol with Pulse */}
                <ParallaxElement speed={0.4} direction="down">
                    <motion.div
                        animate={pulseAnimation}
                        style={{
                            x: useTransform(springX, [-10, 10], [-8, 8]),
                            y: useTransform(springY, [-10, 10], [-8, 8])
                        }}
                        className="absolute top-1/2 left-1/3 text-brand-dark-red text-6xl opacity-10"
                    >
                        ॐ
                    </motion.div>
                </ParallaxElement>

                {/* Additional floating elements */}
                <motion.div
                    animate={{
                        rotate: [0, 360],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute top-1/4 right-1/4 w-16 h-16 border-2 border-brand-gold rounded-full opacity-10"
                />

                <motion.div
                    animate={{
                        rotate: [360, 0],
                        scale: [1, 0.9, 1],
                    }}
                    transition={{
                        duration: 12,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute bottom-1/3 right-1/3 w-12 h-12 border-2 border-brand-dark-red rounded-full opacity-15"
                />
            </div>

            {/* Main Content Container with Parallax */}
            <motion.div
                style={{ y: textY }}
                className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 min-h-screen"
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
                    {/* Left Side - Text Content */}
                    <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        animate="visible"
                        className="space-y-8"
                    >
                        {/* Main Title with Enhanced Animation */}
                        <motion.h1
                            variants={{
                                hidden: { opacity: 0, y: 50, scale: 0.9 },
                                visible: {
                                    opacity: 1,
                                    y: 0,
                                    scale: 1,
                                    transition: {
                                        type: "spring",
                                        stiffness: 100,
                                        damping: 15,
                                        delay: 0.2
                                    }
                                }
                            }}
                            className="font-cinzel text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
                        >
                            <span className="text-brand-dark-red">{heroContent.title}:</span>
                        </motion.h1>

                        {/* Tagline with Typewriter Effect */}
                        <motion.h2
                            variants={{
                                hidden: { opacity: 0, x: -50 },
                                visible: {
                                    opacity: 1,
                                    x: 0,
                                    transition: {
                                        duration: 0.8,
                                        delay: 0.4
                                    }
                                }
                            }}
                            className="font-cinzel text-2xl sm:text-3xl lg:text-4xl text-brand-dark-red font-medium leading-tight"
                        >
                            {heroContent.tagline}
                        </motion.h2>

                        {/* Subtitle */}
                        <motion.p
                            variants={fadeInUp}
                            className="font-poppins text-lg sm:text-xl text-gray-600 leading-relaxed max-w-lg"
                        >
                            {heroContent.subtitle}
                        </motion.p>

                        {/* Enhanced CTA Buttons */}
                        <motion.div
                            variants={fadeInUp}
                            className="flex flex-col sm:flex-row gap-4"
                        >
                            <motion.button
                                whileHover={{
                                    scale: 1.05,
                                    boxShadow: "0 10px 25px rgba(255, 215, 0, 0.3)"
                                }}
                                whileTap={{ scale: 0.95 }}
                                animate={{
                                    boxShadow: [
                                        "0 4px 15px rgba(255, 215, 0, 0.2)",
                                        "0 6px 20px rgba(255, 215, 0, 0.3)",
                                        "0 4px 15px rgba(255, 215, 0, 0.2)"
                                    ]
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                                onClick={onTrialClick}
                                className="bg-brand-gold text-gray-900 px-6 py-3 rounded-lg font-poppins font-semibold text-base shadow-lg hover:bg-yellow-500 hover:shadow-xl transition-all duration-300"
                            >
                                {heroContent.primaryCTA}
                            </motion.button>

                            <motion.button
                                whileHover={{
                                    scale: 1.05,
                                    borderColor: "#8B0000",
                                    backgroundColor: "rgba(139, 0, 0, 0.1)"
                                }}
                                whileTap={{ scale: 0.95 }}
                                onClick={onViewPlansClick}
                                className="border-2 border-brand-dark-red text-brand-dark-red hover:bg-brand-dark-red hover:text-white px-6 py-3 rounded-lg font-poppins font-semibold text-base transition-all duration-300"
                            >
                                {heroContent.secondaryCTA}
                            </motion.button>
                        </motion.div>

                        {/* Enhanced Trust Indicators with Animated Counters */}
                        <motion.div
                            variants={fadeInUp}
                            className="grid grid-cols-3 gap-6 pt-8"
                        >
                            <motion.div
                                className="text-center"
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <div className="text-2xl lg:text-3xl font-bold text-brand-dark-red mb-1">
                                    <AnimatedCounter value={500} suffix="+" />
                                </div>
                                <div className="font-poppins text-sm text-gray-600">Happy Students</div>
                            </motion.div>
                            <motion.div
                                className="text-center"
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <div className="text-2xl lg:text-3xl font-bold text-brand-dark-red mb-1">
                                    <AnimatedCounter value={50} suffix="+" />
                                </div>
                                <div className="font-poppins text-sm text-gray-600">Expert Instructors</div>
                            </motion.div>
                            <motion.div
                                className="text-center"
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <div className="text-2xl lg:text-3xl font-bold text-brand-dark-red mb-1">
                                    <AnimatedCounter value={25} suffix="+" />
                                </div>
                                <div className="font-poppins text-sm text-gray-600">Countries Served</div>
                            </motion.div>
                        </motion.div>
                    </motion.div>

                    {/* Right Side - Enhanced Image with Parallax */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative"
                    >
                        <ParallaxElement speed={0.2} direction="up">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-brand-dark-red/10 to-brand-gold/10">
                                <motion.div
                                    className="w-full h-[500px] lg:h-[600px] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <img
                                        src={heroContent.backgroundImage}
                                        alt="Live Learning Experience"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                        }}
                                    />
                                    {/* Fallback content when image fails to load */}
                                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 hidden">
                                        <div className="text-center p-8">
                                            <motion.div
                                                className="w-24 h-24 bg-brand-dark-red rounded-full flex items-center justify-center mx-auto mb-4"
                                                animate={{ rotate: [0, 360] }}
                                                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                            >
                                                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                                </svg>
                                            </motion.div>
                                            <h3 className="font-cinzel text-xl font-bold text-gray-700 mb-2">Live Learning Experience</h3>
                                            <p className="font-poppins text-gray-600">Personalized 1-on-1 classes with expert instructors</p>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Overlay with subtle gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>

                                {/* Enhanced Floating badge */}
                                <motion.div
                                    initial={{ scale: 0, rotate: -180 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    transition={{
                                        delay: 1,
                                        type: "spring",
                                        stiffness: 200,
                                        duration: 0.8
                                    }}
                                    whileHover={{ scale: 1.1, y: -5 }}
                                    className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg"
                                >
                                    <div className="flex items-center space-x-2">
                                        <motion.div
                                            className="w-3 h-3 bg-green-500 rounded-full"
                                            animate={{ scale: [1, 1.2, 1] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        />
                                        <span className="font-poppins text-sm font-medium text-gray-800">Live Classes</span>
                                    </div>
                                </motion.div>

                                {/* Enhanced Bottom info card */}
                                <motion.div
                                    initial={{ y: 50, opacity: 0, scale: 0.9 }}
                                    animate={{ y: 0, opacity: 1, scale: 1 }}
                                    transition={{
                                        delay: 1.2,
                                        duration: 0.6,
                                        type: "spring",
                                        stiffness: 100
                                    }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                    className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-poppins text-sm font-semibold text-gray-800">Next Class</p>
                                            <p className="font-poppins text-xs text-gray-600">Hindustani Vocals • 6:00 PM</p>
                                        </div>
                                        <div className="flex -space-x-2">
                                            <motion.div
                                                className="w-8 h-8 bg-brand-dark-red rounded-full flex items-center justify-center text-white text-xs font-bold"
                                                whileHover={{ scale: 1.2, z: 10 }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                A
                                            </motion.div>
                                            <motion.div
                                                className="w-8 h-8 bg-brand-gold rounded-full flex items-center justify-center text-gray-800 text-xs font-bold"
                                                whileHover={{ scale: 1.2, z: 10 }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                S
                                            </motion.div>
                                            <motion.div
                                                className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                                whileHover={{ scale: 1.2, z: 10 }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                +3
                                            </motion.div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </ParallaxElement>

                        {/* Enhanced Decorative elements around image */}
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                            className="absolute -top-4 -right-4 w-16 h-16 border-4 border-brand-gold rounded-full opacity-30"
                            style={{
                                filter: "drop-shadow(0 0 10px rgba(255, 215, 0, 0.3))"
                            }}
                        />

                        <motion.div
                            animate={{ rotate: -360 }}
                            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                            className="absolute -bottom-4 -left-4 w-12 h-12 border-4 border-brand-dark-red rounded-full opacity-30"
                            style={{
                                filter: "drop-shadow(0 0 8px rgba(139, 0, 0, 0.3))"
                            }}
                        />

                        {/* Additional floating decorative elements */}
                        <motion.div
                            animate={{
                                y: [0, -20, 0],
                                rotate: [0, 10, 0],
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            className="absolute top-1/4 -left-8 text-brand-gold text-2xl opacity-20"
                        >
                            ✨
                        </motion.div>

                        <motion.div
                            animate={{
                                y: [0, 15, 0],
                                rotate: [0, -15, 0],
                            }}
                            transition={{
                                duration: 5,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: 1,
                            }}
                            className="absolute bottom-1/4 -right-8 text-brand-dark-red text-2xl opacity-20"
                        >
                            🎵
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Enhanced Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.8 }}
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
            >
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex flex-col items-center cursor-pointer group"
                    onClick={() => {
                        document.getElementById('courses')?.scrollIntoView({
                            behavior: 'smooth'
                        });
                    }}
                >
                    {/* Circle with arrow */}
                    <motion.div
                        className="w-12 h-12 border-2 border-white/60 rounded-full flex items-center justify-center bg-white/10 backdrop-blur-sm group-hover:border-brand-gold group-hover:bg-brand-gold/20 transition-all duration-300"
                        whileHover={{ scale: 1.1 }}
                        animate={{
                            boxShadow: [
                                "0 0 0 0 rgba(255, 255, 255, 0.3)",
                                "0 0 0 10px rgba(255, 255, 255, 0)",
                                "0 0 0 0 rgba(255, 255, 255, 0)"
                            ]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        {/* Arrow pointing down */}
                        <motion.svg
                            className="w-5 h-5 text-white group-hover:text-brand-gold transition-colors duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            animate={{ y: [0, 3, 0] }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            />
                        </motion.svg>
                    </motion.div>

                    {/* Scroll text */}
                    <motion.p
                        className="text-white/70 text-xs font-poppins mt-2 group-hover:text-white transition-colors duration-300"
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        Scroll
                    </motion.p>
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero; 
